import { computed, inject, Injectable, signal } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Data,
  NavigationEnd,
  PRIMARY_OUTLET,
  Router,
  UrlSegmentGroup,
} from '@angular/router';
import { filter } from 'rxjs/operators';

import { Breadcrumb } from 'src/app/components/pagetitle/pagetitle.types';
import { FILING_ROUTES } from 'src/app/pages/filing/filing-routes.constants';

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService {
  /**
   * The list a detail route's leading breadcrumb should return to, keyed by that
   * breadcrumb's label. A detail route's breadcrumb is an array — for example
   * `['Clerk Review', 'Transaction 123']` — and every item otherwise resolves to the
   * detail route's own URL, so without this the leading crumb is a no-op link.
   */
  private static readonly LIST_URL_BY_LABEL: Record<string, string> = {
    'Clerk Review': '/clerk-review-list',
    'Judge Review': '/judge-review-list',
    'Batches for Clerk Review': '/batch-review-list',
  };

  private router = inject(Router);

  // Optional label to add after 'Existing Case' in the breadcrumb trail
  private readonly _existingCaseLabel = signal<string | null>(null);

  private _activePrimaryUrl = signal<string>('');
  activePrimaryUrl = this._activePrimaryUrl.asReadonly();

  private _breadcrumbs = signal<Breadcrumb[]>([]);
  readonly breadcrumbs = computed(() => {
    const breadcrumbList = [...this._breadcrumbs()];
    const caseLabel = this._existingCaseLabel();

    if (!caseLabel) {
      return breadcrumbList;
    }

    const index = breadcrumbList.findIndex(bc => bc.label === 'Existing Case');
    if (index !== -1) {
      breadcrumbList[index].url = `/filing/${FILING_ROUTES.EXISTING_CASE}`;
      breadcrumbList.splice(index + 1, 0, { label: caseLabel, url: '' });
    }

    return breadcrumbList;
  });

  constructor() {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        const urlTree = this.router.parseUrl(this.router.url);
        this.removeSecondaryOutlets(urlTree.root);
        const url = this.router.serializeUrl(urlTree);
        this._activePrimaryUrl.set(url);

        const root = this.router.routerState.snapshot.root;
        const breadcrumbs: Breadcrumb[] = [];
        this.addBreadcrumb(root, [], breadcrumbs);
        this._breadcrumbs.set(breadcrumbs);
      });
  }

  /**
   * Sets the case label to inject into the breadcrumb trail after 'Existing Case'.
   * Pass null to remove the case label breadcrumb.
   */
  setExistingCaseLabel(label: string | null) {
    this._existingCaseLabel.set(label);
  }

  /**
   * Update the last breadcrumb item with new data (useful for dynamic updates after API calls)
   * @param newLabel The new label for the last breadcrumb item
   */
  updateLastBreadcrumb(newLabel: string): void {
    const currentBreadcrumbs = this._breadcrumbs();
    if (currentBreadcrumbs.length > 0) {
      const updatedBreadcrumbs = [...currentBreadcrumbs];
      const lastBreadcrumb = updatedBreadcrumbs[updatedBreadcrumbs.length - 1];

      // If the last breadcrumb is part of an array, update all array items with the new label
      if (lastBreadcrumb.isArrayItem && lastBreadcrumb.arrayLength) {
        const arrayStartIndex =
          updatedBreadcrumbs.length - lastBreadcrumb.arrayLength;
        for (let i = arrayStartIndex; i < updatedBreadcrumbs.length; i++) {
          if (i === arrayStartIndex + 1) {
            // Update the second item in the array (Transaction ID part)
            updatedBreadcrumbs[i] = {
              ...updatedBreadcrumbs[i],
              label: newLabel,
            };
          }
        }
      } else {
        // Update the last breadcrumb
        updatedBreadcrumbs[updatedBreadcrumbs.length - 1] = {
          ...lastBreadcrumb,
          label: newLabel,
        };
      }

      this._breadcrumbs.set(updatedBreadcrumbs);
    }
  }

  /**
   * Function to construct the breadcrumb hierarchy for the current route
   * @param route
   * @param parentUrl
   * @param breadcrumbs
   */
  private addBreadcrumb(
    route: ActivatedRouteSnapshot,
    parentUrl: string[],
    breadcrumbs: Breadcrumb[],
  ) {
    if (route) {
      // Construct the route URL
      const routeUrl = parentUrl.concat(route.url.map(url => url.path));

      // Add an element for the current route part
      if (route.data['breadcrumb']) {
        const breadcrumbData = this.getLabel(route.data, route);
        const breadcrumbUrl = '/' + routeUrl.join('/');

        // Handle array breadcrumbs by creating multiple breadcrumb items
        if (Array.isArray(breadcrumbData)) {
          breadcrumbData.forEach((label, index) => {
            // An array breadcrumb labels one route, so every item would otherwise link to
            // that route's own URL. The first item names the list the route was reached
            // from, so it points at that list. Each item after it names a level of the
            // route's own path — e.g. `['Judge Review', 'Transaction 123', 'Review & Sign']`
            // on `/judge-review/:id/review-sign` — so it drops one trailing segment per
            // position from the end: the transaction crumb links to `/judge-review/:id`.
            // Kept to at least one segment: a negative `slice` end would count from the end
            // and produce an unrelated URL on a route shorter than its breadcrumb.
            const segmentsToKeep = Math.max(
              1,
              routeUrl.length - (breadcrumbData.length - 1 - index),
            );
            const url =
              index === 0
                ? (BreadcrumbService.LIST_URL_BY_LABEL[label] ?? breadcrumbUrl)
                : '/' + routeUrl.slice(0, segmentsToKeep).join('/');

            breadcrumbs.push({
              label,
              url,
              isArrayItem: true,
              arrayIndex: index,
              arrayLength: breadcrumbData.length,
            });
          });
        } else {
          breadcrumbs.push({
            label: breadcrumbData,
            url: breadcrumbUrl,
            isArrayItem: false,
          });
        }
      }

      // Add another element for the next route part
      this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs);
    }
  }

  private getLabel(data: Data, route?: ActivatedRouteSnapshot) {
    // The breadcrumb can be defined as a static string or as a function to construct the breadcrumb element out of the route data
    return typeof data['breadcrumb'] === 'function'
      ? data['breadcrumb'](data, route)
      : data['breadcrumb'];
  }

  /**
  /* Remove secondary router outlets from URL tree and return the primary navigation path.
  /* This allows named outlet urls to be matched as the active router link.
  **/
  private removeSecondaryOutlets(group: UrlSegmentGroup): void {
    if (group.children[PRIMARY_OUTLET]) {
      group.children = { [PRIMARY_OUTLET]: group.children[PRIMARY_OUTLET] };
      this.removeSecondaryOutlets(group.children[PRIMARY_OUTLET]);
    }
  }
}
